import { useEffect, useMemo, useRef, useState } from 'react';

import { Auth0ContextInterface, User } from '@auth0/auth0-react';
import { HRISProviderSlugEnum } from '@electricjs/core_entity-client';
import type {
  PublicSignupBillingAddress,
  PublicSignupRequest,
} from '@electricjs/core_entity-client';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';

import type { Address } from '@/components/AddressGroupField';
import PageSpinner from '@/components/PageSpinner';
import { InitFailedState } from '@/components/Errors/InitFailedState';
import {
  usePostMessageToParent,
  EmbeddedFirstLoginMessageType,
} from '@/hooks/postMessageToParent';
import { useCreateGenericHRISOrganizationMutation } from '@/redux/slices/signupApiSlice';
import { IsolvedAuthParamKeys, LOGIN_HINT_PARAM } from '@/types/queryParamKeys';

import {
  parseEmbeddedFirstLoginArgs,
  type Auth0UserWithEmbeddedClaims,
  type EmbeddedFirstLoginArgs,
} from './parseEmbeddedFirstLoginArgs';

export type { EmbeddedFirstLoginArgs } from './parseEmbeddedFirstLoginArgs';

// LaunchDarkly's <LDProvider> renders children before the client's first flag
// fetch resolves, so useFlags() can read stale/default values briefly on
// mount. This bounds how long we wait for real values before proceeding
// anyway with whatever's currently available.
const LD_INIT_TIMEOUT_SECONDS = 10;

// Some connections need extra /authorize params on the re-login fired after
// signup completes — so we rebuild them here from claims captured on the first
// login rather than relying on the (by-then-stale) page URL.
// - isolved's post-login Action requires tenantId + clientId.
// - paychex needs the login_hint it received on the first login, which is
//   the external company id.
// Deliberately omits `connection`: like every other embedded relogin, we want
// Auth0 to reuse the existing session cookie rather than redirect to the IdP,
// which would fail in the iframe (see useDestroyExistingSSOSession).
function getReloginAuthorizationParams(
  args: EmbeddedFirstLoginArgs
): Record<string, string> {
  if (args.hrisProviderSlug === HRISProviderSlugEnum.Paychex) {
    return { [LOGIN_HINT_PARAM]: args.externalCompanyId };
  }
  if (
    args.hrisProviderSlug !== HRISProviderSlugEnum.Isolved ||
    !args.externalTenantId
  ) {
    return {};
  }
  return {
    [IsolvedAuthParamKeys.TenantId]: args.externalTenantId,
    [IsolvedAuthParamKeys.ClientId]: args.externalCompanyId,
  };
}

function getExternalAccountIdentifier(args: EmbeddedFirstLoginArgs): string {
  if (args.hrisProviderSlug === HRISProviderSlugEnum.Isolved) {
    return `${args.externalTenantId}|${args.externalCompanyId}`;
  }
  return args.externalCompanyId;
}

function toSignupBillingAddress(address: Address): PublicSignupBillingAddress {
  const line2 =
    typeof address.streetAddress2 === 'string' &&
    address.streetAddress2.trim() !== ''
      ? address.streetAddress2
      : undefined;
  return {
    line1: address.streetAddress1,
    ...(line2 !== undefined ? { line2 } : {}),
    city: address.city,
    state: address.state,
    zip_code: address.zip,
    country_code: address.country,
  };
}

type EmbeddedFirstLoginProps = {
  auth0User: User | null | undefined;
  getIdTokenClaims: Auth0ContextInterface['getIdTokenClaims'];
  onSignupSuccess: (authorizationParams?: Record<string, string>) => void;
};

export const EmbeddedFirstLogin = ({
  auth0User,
  getIdTokenClaims,
  onSignupSuccess,
}: EmbeddedFirstLoginProps) => {
  const { idTokenAdminRightsCheck } = useFlags();
  const ldClient = useLDClient();
  const [isFlagsReady, setIsFlagsReady] = useState(false);
  const [idTokenError, setIdTokenError] = useState(false);
  const [signUp, { isSuccess, isError, isLoading }] =
    useCreateGenericHRISOrganizationMutation();
  // Set to true synchronously before calling signUp() so the StrictMode
  // double-invocation of effects (cleanup → setup with the same stale closure)
  // sees it as true and returns early. Never reset — no retries.
  const signupAttemptedRef = useRef(false);
  const signupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // usePostMessageToParent returns a useCallback(fn, []) — stable reference.
  const postMessageToParent = usePostMessageToParent();

  // Depend on auth0User.sub (stable primitive) rather than the full object,
  // which Auth0 can recreate on token refresh with identical claims.
  const parsedArgs = useMemo(
    () => parseEmbeddedFirstLoginArgs(auth0User as Auth0UserWithEmbeddedClaims),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth0User?.sub]
  );

  // Boolean primitive extracted so the effect dep array holds no object.
  const isValidAdmin = parsedArgs !== null && parsedArgs.isAdmin;

  // Pre-built payload so signUp() can be called directly without reading
  // from parsedArgs inside the effect. Recomputes only when parsedArgs changes
  // (i.e. when auth0User.sub changes — same effective stability as parsedArgs).
  const signUpPayload = useMemo(() => {
    if (!parsedArgs?.isAdmin) return null;
    return {
      organization_name: parsedArgs.externalCompanyName,
      first_name: parsedArgs.firstName,
      last_name: parsedArgs.lastName,
      user_email: parsedArgs.email,
      hris_provider_slug: parsedArgs.hrisProviderSlug,
      external_account_identifier: getExternalAccountIdentifier(parsedArgs),
      ...(parsedArgs.address && {
        billing_address: toSignupBillingAddress(parsedArgs.address),
      }),
    };
  }, [parsedArgs]);

  // Rebuilt from claims (not the URL) so it's still available after the
  // callback navigation, which strips the original query params.
  const reloginAuthorizationParams = useMemo(() => {
    if (!parsedArgs) return {};
    return getReloginAuthorizationParams(parsedArgs);
  }, [parsedArgs]);

  // <LDProvider> renders before the client's first flag fetch resolves, so
  // idTokenAdminRightsCheck can briefly read as false on mount. Wait for the
  // client to settle (or time out) before letting the signup effect below
  // latch signupAttemptedRef on a possibly-stale flag value.
  useEffect(() => {
    if (!ldClient) return;
    let cancelled = false;
    ldClient
      .waitForInitialization(LD_INIT_TIMEOUT_SECONDS)
      .catch(error => {
        // Timed out or failed to initialize — proceed with whatever flag
        // value is currently available rather than hanging the signup flow.
        console.error(
          'Timed out waiting for LaunchDarkly to initialize in EmbeddedFirstLogin'
        );
        window.DD_RUM?.addError(error, {
          location: 'EmbeddedFirstLogin',
        });
      })
      .then(() => {
        if (!cancelled) setIsFlagsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [ldClient]);

  useEffect(() => {
    if (
      !isValidAdmin ||
      !signUpPayload ||
      !isFlagsReady ||
      signupAttemptedRef.current ||
      isLoading ||
      isSuccess ||
      isError
    ) {
      return;
    }
    signupAttemptedRef.current = true;

    const runSignUp = (payload: PublicSignupRequest) => {
      signUp(payload)
        .unwrap()
        .then(response => {
          postMessageToParent({
            type: EmbeddedFirstLoginMessageType.SIGNUP_COMPLETE,
            organization_id: response.organization_id,
            external_organization_id: response.external_organization_id,
            timestamp: new Date().toISOString(),
          });
          // Defer navigation to next macrotask so the postMessage event is delivered
          // to the parent frame before the iframe navigates away (loginWithRedirect).
          signupTimerRef.current = setTimeout(
            () => onSignupSuccess(reloginAuthorizationParams),
            0
          );
        })
        .catch(() => {
          // RTK Query sets isError:true in Redux state, which causes <InitFailedState />
          // to render on the next render cycle. Empty catch prevents an unhandled
          // promise rejection browser warning from surfacing.
        });
    };

    if (idTokenAdminRightsCheck) {
      getIdTokenClaims()
        .then(idTokenClaims => {
          runSignUp({ ...signUpPayload, id_token: idTokenClaims?.__raw ?? '' });
        })
        .catch(error => {
          console.error(
            'Failed to fetch id token claims for JIT signup in EmbeddedFirstLogin'
          );
          window.DD_RUM?.addError(error, {
            location: 'EmbeddedFirstLogin',
          });
          setIdTokenError(true);
        });
    } else {
      runSignUp(signUpPayload);
    }
  }, [
    isValidAdmin,
    signUpPayload,
    isFlagsReady,
    signUp,
    getIdTokenClaims,
    idTokenAdminRightsCheck,
    onSignupSuccess,
    reloginAuthorizationParams,
    postMessageToParent,
    isLoading,
    isSuccess,
    isError,
  ]);

  // No setup logic — only the cleanup runs, exactly once on unmount.
  // Cancels the deferred onSignupSuccess call if the component is torn down
  // before the setTimeout fires (e.g. parent unmounts mid-render cycle).
  useEffect(() => {
    return () => {
      if (signupTimerRef.current !== null) {
        clearTimeout(signupTimerRef.current);
      }
    };
  }, []);

  if (isError || idTokenError || !auth0User || !isValidAdmin) {
    return <InitFailedState />;
  }

  return <PageSpinner />;
};
